import ConfigManager from "./ConfigManager";
import Logger from "./Logger";
import TargetingManager from "./TargetingManager";
import pbjs from "./pbjs";
import googletag from "./googletag";
import PrebidManager from "./PrebidManager";
import GoogletagManager from "./GoogletagManager";
import Auction from "./Auction";
import ScreenDetector from "./ScreenDetector";
import { AuctionConfig, SlotUnion } from "./Types";

class Mastodon {
  private initialized: boolean = false;
  private taskQueue: any[] = [];
  private firstTaskRun: boolean = false;

  constructor() {
    this.initialized = false;
    pbjs().que.push(() => Logger.log("PBJS loaded", pbjs()));
    googletag().cmd.push(() => Logger.log("Googletag loaded", googletag()));
  }

  /**
   * Initializes
   * @param site The name of the property or site
   * @param env The envronment name (stage, prod)
   * @param platform The platform the user is visiting from (mobile, desktop)
   */
  async init(site: string, env: string, platform?: string) {
    Logger.group("Initializing");
    console.log(
      "init params:",
      site,
      env,
      platform || ScreenDetector.desktopOrMobile()
    );
    await ConfigManager.fetchConfig(
      site,
      env,
      platform || ScreenDetector.desktopOrMobile()
    );
    PrebidManager.configure();

    Logger.endGroup();
    pbjs().que.push(() => {
      googletag().cmd.push(() => {
        this.initialized = true;
        Logger.log("Running queued tasks after init", this.taskQueue);
        this.executeNextTask();
      });
    });
  }

  /**
   * @hidden
   */
  checkInitialized() {
    if (!this.initialized) throw new Error("Mastodon not initialized yet!");
  }

  /**
   * adds the keys to global targeting
   * @param t
   */
  addGlobalTargeting(t: Object) {
    this.checkInitialized();
    TargetingManager.addGlobalTargeting(t);
  }

  /**
   * replaces all global targeting params with the new ones
   * @param t
   */
  replaceGlobalTargeting(t: Object) {
    this.checkInitialized();
    TargetingManager.replaceGlobalTargeting(t);
  }

  /**
   * Adds the keys in `t` to the `adUnit` config
   * @param slot the container id or slot object for the slot
   * @param t object container targeting keys and values
   */
  addSlotTargeting(slot: SlotUnion, t: object) {
    this.checkInitialized();
    TargetingManager.addSlotTargeting(slot, t);
  }

  /**
   * Replaces the `containerId` config with the keys and values in `t`
   * @param slot the container id or slot object for the slot
   * @param t object container targeting keys and values
   */
  replaceSlotTargeting(slot: SlotUnion, t: object) {
    this.checkInitialized();
    TargetingManager.replaceSlotTargeting(slot, t);
  }

  /**
   * Changes the platform
   * @param platform 'mobile' or 'desktop'
   */
  setPlatform(platform: string) {
    ConfigManager.setPlatform(platform);
  }

  /**
   * Enables debug logging
   */
  enableDebug() {
    Logger.setDebugLogging(true);
  }

  /**
   * Adds a new ad slot to the page
   *
   * @param adUnit The ad unit to use for the slot
   * @param containerId The id of the div to use as a container for the slot
   * @returns
   */
  addSlot(adUnit: string, containerId: string) {
    return GoogletagManager.addSlot(adUnit, containerId);
  }

  /**
   * Returns the full ad unit path for the specified ad unit
   * @param adUnit
   */
  getAdunitPath(adUnit: string): string {
    return ConfigManager.getAdUnitPath(adUnit);
  }

  /**
   * Returns the full Mastodon config object
   *
   */
  getFullConfig(): Object {
    return ConfigManager.configData;
  }

  /**
   * Initiates an auction to display(or refresh) the specified ad slots
   * @param slots The slot objects or container ids of the slots to load
   * @param config Optional config
   */
  loadAds(slots?: SlotUnion[], config?: AuctionConfig) {
    const auction = new Auction(slots, config);
    auction.run();
  }

  /**
   * Instructs the service to render the slot
   * @param slotId
   */
  display(slot: SlotUnion) {
    Logger.log("setting display for ", slot);
    googletag().cmd.push(() => {
      googletag().display(slot);
    });
  }

  /**
   * Pushes a javascript function to the queue for execution once mastadon is ready
   * @param {*} func
   */
  push(func: Function) {
    Logger.log("got push", func);
    if (!this.firstTaskRun) {
      Logger.log("executing first task");
      func();
      this.firstTaskRun = true;
    } else {
      this.taskQueue.push(func);
      if (this.initialized) {
        Logger.log("executing non-first task");
        this.executeNextTask();
      }
    }
  }

  /**
   * Returns an array of slot objects for the slot container ids.
   * @param slots Container ID of the slots
   */
  getSlotObjects(slots: string[]): any[] {
    return slots.map(
      (containerID) => GoogletagManager.containerSlotMap[containerID]
    );
  }

  /**
   * Clears all slots content for the given slot container ids
   * @param slotContainerIds Container IDs of the slots
   *
   */
  clearSlotsById(slotContainerIds: string[]): boolean {
    const slots = this.getSlotObjects(slotContainerIds);
    Logger.log("Clearing slots", slotContainerIds, slots);
    return googletag().pubads().clear(slots);
  }

  /**
   * Clears all content for the given slots
   * @param slots The slot objects to clear
   * @returns
   */
  clearSlots(slots?: googletag.Slot[]): boolean {
    return googletag().pubads().clear(slots);
  }

  /**
   * @hidden
   */
  executeNextTask() {
    const task = this.taskQueue.shift();
    Logger.log("executeNextTask() called", task);
    if (task) {
      Logger.group("task exists. executing");
      task();
      Logger.log("task execution complete");
      Logger.endGroup();
    } else {
      Logger.log("task is falsy");
    }
    if (this.taskQueue.length) this.executeNextTask();
  }

  /**
   *
   * @hidden
   * @param tasks
   */
  addExistingTasks(tasks) {
    tasks.forEach((t) => this.push(t));
  }
}
export type MastodonUnion = [] | Mastodon;
export * from "./Types";

export default Mastodon;
